import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { PROPERTY_TYPE_LABELS } from '@wasity/shared';
import { Button, Card, Chip, ConfirmDialog, DateText, ErrorNotice, KV, Notice, PageHeader, Skeleton, Table } from '@/components/ui';
import { useI18n, formatDateTime, formatRelative } from '@/i18n';
import { api } from '@/lib/api';
import { errorText } from '@/lib/errors';
import { money } from '@/lib/format';
import type { AuditRow, EvDetail } from '@/lib/types';
import { EvStepper, PersonCard, RequestTimeline, SlotLine } from '@/features/shared/RequestParts';
import { AdminEvChip, ratePct, realEmail, settingValue, useSettings } from './common';

export function AdminEvaluationDetail() {
  const { id = '' } = useParams();
  const { t, locale, localTitle } = useI18n();
  const qc = useQueryClient();
  const detail = useQuery({ queryKey: ['admin', 'ev', id], queryFn: () => api.get<EvDetail>(`/api/admin/evaluations/${id}`) });
  const audit = useQuery({ queryKey: ['admin', 'audit', 'EV', id], queryFn: () => api.get<{ items: AuditRow[] }>(`/api/admin/audit?kind=EV&id=${id}`) });
  const settings = useSettings();
  const [dialog, setDialog] = useState<'cancel' | 'refund' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['admin'] }); setDialog(null); setError(null); };
  const fail = (e: unknown) => setError((e as { code?: string }).code ?? 'error');
  const cancel = useMutation({ mutationFn: (reason: string) => api.post(`/api/admin/evaluations/${id}/cancel`, { reason: reason || undefined }), onSuccess: refresh, onError: fail });
  const refund = useMutation({ mutationFn: () => api.post(`/api/admin/evaluations/${id}/refund`), onSuccess: refresh, onError: fail });
  if (detail.isLoading) return <Skeleton lines={10} />;
  if (detail.error || !detail.data) return <ErrorNotice error={detail.error} retry={() => void detail.refetch()} labels={{ message: t('app.error'), retry: t('app.retry') }} />;
  const d = detail.data;
  const ev = d.request;
  const rate = settingValue<number>(settings.data?.items, 'service_fee.rate', 0.05);
  const terminal = ev.status === 'completed' || ev.status === 'cancelled';
  const refundNeeded = !!ev.refundNeededAt && d.payment?.status === 'paid';
  const names = { ...(d.evaluator ? { [d.evaluator.id]: d.evaluator.name } : {}), ...(d.owner ? { [d.owner.id]: d.owner.name } : {}) };
  const paymentChip = d.payment ? <Chip form={d.payment.status === 'paid' ? 'filled' : d.payment.status === 'refunded' ? 'muted' : d.payment.status === 'failed' ? 'warning' : 'outlined'}>{t(`status.${d.payment.status}`)}</Chip> : null;
  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        crumbs={[{ to: '/admin/evaluations', label: t('nav.evaluations') }, { label: ev.number }]}
        title={localTitle(d)}
        chip={<AdminEvChip ev={ev} />}
        meta={<span><span className="num">{ev.number}</span> · {t('app.created')} <DateText at={ev.createdAt} /></span>}
        actions={!terminal ? <Button variant="danger" className="min-h-9 text-[14px]" onClick={() => setDialog('cancel')}>{t('admin.request.cancel')}</Button> : undefined}
      />
      <Card><EvStepper status={ev.status} /></Card>
      {error ? <Notice tone="error">{errorText(error, t)}</Notice> : null}
      <div className="grid lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-6 items-start">
        <div className="flex flex-col gap-4">
          {ev.status === 'new' ? (
            <Notice tone="warning" title={ev.returnedAt ? t('status.expiredReassign') : t('status.awaitingAssignment')}>
              <Link to={`/admin/queue?select=${ev.id}`}>{t('admin.queue.assignEvaluator')}</Link>
            </Notice>
          ) : null}
          {refundNeeded ? (
            <Notice tone="warning" title={t('admin.evaluation.refundNeeded')}>
              <p>{t('admin.evaluation.refundBody', { amount: money(d.payment?.amountHalalas, locale) })}</p>
              <Button variant="primary" className="min-h-9 text-[14px] mt-3" onClick={() => setDialog('refund')}>{t('admin.evaluation.markRefunded')}</Button>
            </Notice>
          ) : null}
          {ev.status === 'cancelled' && !refundNeeded ? <Notice tone="info" title={t('admin.request.cancelled')}>{t('admin.request.cancelledBy', { when: ev.cancelledAt ? formatRelative(ev.cancelledAt, locale) : '', reason: ev.cancelReason ?? '—' })}</Notice> : null}
          <Card>
            <h2 className="mb-4">{t('admin.request.updates')}</h2>
            <RequestTimeline events={d.events} viewer="admin" names={names} />
          </Card>
          <Card>
            <h2 className="mb-3">{t('admin.request.audit')}</h2>
            {audit.data?.items.length ? (
              <Table>
                <tbody>
                  {audit.data.items.map((a) => (
                    <tr key={a.id}><td className="text-muted min-w-[120px]"><DateText at={a.createdAt} wrap /></td><td className="ltr num">{realEmail(a.actorEmail) ?? a.actorName ?? '—'}</td><td className="num">{a.action}</td></tr>
                  ))}
                </tbody>
              </Table>
            ) : <p className="text-muted text-[14px]">{t('app.none')}</p>}
          </Card>
        </div>
        <aside className="flex flex-col gap-3">
          <Card lift>
            <h2 className="mb-3">{t('admin.evaluation.feeAndPayment')}</h2>
            <KV items={[
              { k: t('admin.evaluation.evaluatorFee'), v: money(ev.feeHalalas, locale), num: true },
              { k: t('admin.evaluation.serviceFee', { rate: ratePct(rate) }), v: money(ev.serviceFeeHalalas, locale), num: true },
              { k: t('admin.evaluation.total'), v: <b>{money(ev.totalHalalas, locale)}</b>, num: true },
              { k: t('admin.evaluation.payment'), v: d.payment ? <span><span className="num">{d.payment.number}</span> · {paymentChip}</span> : '—' },
              { k: t('admin.evaluation.method'), v: t('admin.evaluation.inApp') },
            ]} />
          </Card>
          <Card>
            <h2 className="mb-3">{t('app.property')}</h2>
            <KV items={[
              { k: t('app.kind'), v: PROPERTY_TYPE_LABELS[ev.type][locale] },
              { k: locale === 'ar' ? 'المساحة' : 'Area', v: `${ev.areaM2} ${t('app.m2')}`, num: true },
              { k: locale === 'ar' ? 'الموقع' : 'Location', v: <span>{ev.district}، {ev.city} · <a href={`https://maps.google.com/?q=${ev.lat},${ev.lng}`} target="_blank" rel="noreferrer">{t('app.map')}</a></span> },
              { k: t('admin.evaluation.deed'), v: d.deedUrl && !d.deedUrl.startsWith('mock://') ? <a href={d.deedUrl} target="_blank" rel="noreferrer">{t('app.open')}</a> : ev.deedMediaId ? t('dashboard.deedAttached') : '—' },
              ...(ev.notes ? [{ k: t('app.notes'), v: <span className="text-fg-2">«{ev.notes}»</span> }] : []),
            ]} />
          </Card>
          <PersonCard title={t('app.owner')} person={d.owner} action={d.owner ? <Link to={`/admin/users/${d.owner.id}`} className="text-[14px]">{t('app.account')}</Link> : null} />
          <PersonCard title={t('app.evaluator')} person={d.evaluator} subtitle={d.evaluator?.licenceNo ? `${locale === 'ar' ? 'رخصة' : 'Licence'} ${d.evaluator.licenceNo}` : undefined} action={d.evaluator ? <Link to={`/admin/users/${d.evaluator.id}`} className="text-[14px]">{t('app.account')}</Link> : null}>
            {d.evaluatorSlots ? <SlotLine slots={d.evaluatorSlots} className="mt-3" /> : null}
            {!d.evaluator ? <p className="text-muted text-[14px]">{t('admin.evaluation.noEvaluator')}</p> : null}
          </PersonCard>
          <Card>
            <h2 className="mb-2">{t('admin.evaluation.report')}</h2>
            {d.reportUrl ? (
              d.reportUrl.startsWith('mock://') ? <p className="text-[14px] text-fg-2">{t('evaluator.request.uploaded', { time: ev.reportUploadedAt ? formatDateTime(ev.reportUploadedAt, locale) : '' })}</p> : <a href={d.reportUrl} target="_blank" rel="noreferrer">{t('admin.evaluation.openReport')}</a>
            ) : <p className="text-muted text-[14px]">{t('admin.evaluation.reportHint')}</p>}
          </Card>
        </aside>
      </div>
      <ConfirmDialog open={dialog === 'cancel'} onClose={() => setDialog(null)} title={t('admin.evaluation.cancelTitle', { number: ev.number })} body={t('admin.evaluation.cancelBody')} confirmLabel={t('admin.request.cancel')} cancelLabel={t('app.back')} danger reason={{ label: t('app.reasonOptional') }} loading={cancel.isPending} onConfirm={(r) => cancel.mutate(r)} />
      <ConfirmDialog open={dialog === 'refund'} onClose={() => setDialog(null)} title={t('admin.evaluation.markRefunded')} body={t('admin.evaluation.refundBody', { amount: money(d.payment?.amountHalalas, locale) })} confirmLabel={t('app.confirm')} cancelLabel={t('app.back')} loading={refund.isPending} onConfirm={() => refund.mutate()} />
    </div>
  );
}
